import classNames from 'classnames';
import * as Classes from '../common/classes';
import { BaseProps } from '../common/props';
import React, { useEffect, useRef, useState } from 'react';

export interface ITextAreaInputInputProps
  extends BaseProps,
    Omit<
      React.TextareaHTMLAttributes<HTMLTextAreaElement>,
      'intent' | 'small' | 'large'
    > {
  /**
   * Whether the text area should take up the full width of its container.
   */
  fill?: boolean;

  /**
   * Whether the text area should automatically grow vertically to accommodate content.
   */
  growVertically?: boolean;

  /**
   * @param rows - Pokud není uveden počet rows, použije se 4.
   */
  rows?: number;

  /**
   * Ref handler that receives HTML `<textarea>` element backing this component.
   */
  inputRef?: React.Ref<HTMLTextAreaElement>;
  children?: React.ReactNode;
}

/**
 * Komponenta pro editaci víceřádkového textu.
 */

export const TextAreaInput: React.FC<ITextAreaInputInputProps> = (props) => {
  const {
    className,
    fill = true,
    rows = 4,
    disabled,
    readOnly,
    growVertically,
    inputRef,
    dataTest,
    autoFocus,
    ...other
  } = props;

  const [height, setHeight] = useState<number | undefined>(undefined);
  const textareaElement = useRef<HTMLTextAreaElement | null>(null);

  useEffect(() => {
    if (growVertically && textareaElement.current !== null) {
      setHeight(textareaElement.current.scrollHeight);
    }
  }, [growVertically]);

  useEffect(() => {
    if (inputRef) {
      if (typeof inputRef === 'function') {
        inputRef(textareaElement.current);
      } else if (inputRef) {
        (
          inputRef as React.MutableRefObject<HTMLTextAreaElement | null>
        ).current = textareaElement.current;
      }
    }
  }, [inputRef]);

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    if (growVertically) {
      setHeight(e.target.scrollHeight);
    }
    if (props.onChange) {
      props.onChange(e);
    }
  };

  const rootClasses = classNames(
    className,
    Classes.FIELD_INPUT,
    Classes.TEXT_AREA_INPUT,
    Classes.INPUT,
    {
      [Classes.FIELD_INPUT_DISABLED]: disabled,
      [Classes.FIELD_INPUT_READONLY]: readOnly,
      [Classes.TEXT_AREA_FILL]: fill,
    }
  );

  let { style = {} } = other;
  if (growVertically && height != null) {
    style = {
      ...style,
      height: `${height}px`,
    };
  }

  return (
    <textarea
      data-test={dataTest ? `input-${dataTest}` : undefined}
      {...other}
      className={rootClasses}
      rows={rows}
      ref={textareaElement}
      style={style}
      disabled={disabled}
      readOnly={readOnly}
      onChange={handleChange}
      autoFocus={autoFocus}
    />
  );
};
